import React from "react";
import { Controller, ControllerRenderProps, FieldValues, useForm } from "react-hook-form";
import { toast } from "react-toastify";
import classNames from "classnames";
import { PrimaryButton } from "@components/UI/Buttons/PrimaryButton/PrimaryButton";
import TextInput from "@components/UI/Inputs/TextInput/TextInput";
import useIsMobile from "@src/hooks/useIsMobile";
import { useSendCallbackMutation } from "@app/store/callbackFormApi";
import {
    emailValidationRules,
    messageValidationRules,
    nameValidationRules,
    phoneValidationRules
} from "@features/callback-form/callback-form.validations";
import { PhoneInput } from "@shared/ui/inputs/phone-input";
import TextAreaInput from "@shared/ui/inputs/text-area-input/text-area-input";
import { ContactsPageProps, ERROR_MESSAGE, SUCCESS_MESSAGE, TEXT_INPUT_STYLE } from "./constants";
import classes from "./contacts-page.module.scss";

const ContactsPage = ({ title, showCompanyCard }: ContactsPageProps) => {
    const {
        handleSubmit,
        formState: { errors },
        control
    } = useForm();

    const isMobile = useIsMobile();
    const [sendCallbackData] = useSendCallbackMutation();

    const buttonStyle = {
        marginTop: 10,
        marginBottom: 10,
        height: 36,
        fontSize: 13,
        width: isMobile ? "100%" : 115
    };

    const onSubmit = async (e: React.MouseEvent | React.KeyboardEvent<HTMLDivElement>) => {
        e.preventDefault();
        const submit = handleSubmit(async (data) => {
            const result = await sendCallbackData({
                name: data.Name,
                email: data.Email,
                phoneNumber: data.Phone,
                message: data.Message
            });

            if (result && "error" in result) {
                toast.error(ERROR_MESSAGE, {
                    autoClose: 5000
                });
            } else {
                toast.success(SUCCESS_MESSAGE, {
                    autoClose: 5000
                });
            }
        });
        submit();
    };

    const onEmailChange = (field: ControllerRenderProps<FieldValues, "Email">) => {
        return (val: string | number | undefined) => {
            if (val) {
                field.onChange(val.toString().replace(/\s/g, ""));
            } else {
                field.onChange("");
            }
        };
    };

    const showError = (errName: string) => {
        const errMsg = errors[errName]?.message;
        return <span className={classes.Error}>{errMsg && errMsg.toString()}</span>;
    };

    return (
        <div className={classes.FormScreen}>
            {!isMobile && <h1>{title || "Контакты"}</h1>}

            <div className={classes.FormCont}>
                {showCompanyCard && (
                    <>
                        <label className={classes.Label}>Связаться c нами</label>

                        <div className={classes.Table}>
                            <div>
                                <div>Организация</div>
                                <div>ООО «Гонзо»</div>
                            </div>
                            <div>
                                <div>ИНН</div>
                                <div>9731042980</div>
                            </div>
                            <div>
                                <div>ОГРН</div>
                                <div>1197746350240</div>
                            </div>
                            <div>
                                <div>E-mail</div>
                                <div>Support@likvi.com</div>
                            </div>
                            <div>
                                <div>Адрес</div>
                                <div>
                                    121354, город Москва, Можайское ш., д. 34a, этаж 2 помещение I
                                </div>
                            </div>
                        </div>
                    </>
                )}

                <label className={classes.Label}>Обратная связь</label>
                <div className={classes.FormWrapper}>
                    <div className={classes.Line}>
                        <div className={classes.Circle} />
                        <div className={classes.Circle} />
                        <div className={classes.Circle} />
                        <div className={classes.Circle} />
                    </div>
                    <form className={classes.Form}>
                        <div className={classNames(classes.InputCont, classes.InputContRequired)}>
                            <label>Имя</label>
                            <Controller
                                control={control}
                                name="Name"
                                rules={nameValidationRules}
                                render={({ field }) => {
                                    return (
                                        <TextInput
                                            value={field.value}
                                            onChange={field.onChange}
                                            style={TEXT_INPUT_STYLE}
                                            placeholder="Введите Ваше ФИО"
                                        />
                                    );
                                }}
                            />
                            {errors.Name && showError("Name")}
                        </div>

                        <div className={classNames(classes.InputCont, classes.InputContRequired)}>
                            <label>Электронная почта</label>
                            <Controller
                                control={control}
                                name="Email"
                                rules={emailValidationRules}
                                render={({ field }) => {
                                    return (
                                        <TextInput
                                            value={field.value}
                                            onChange={onEmailChange(field)}
                                            style={TEXT_INPUT_STYLE}
                                            placeholder="Введите электронную почту"
                                        />
                                    );
                                }}
                            />
                            {errors.Email && (
                                <span className={classes.Error}>
                                    {errors?.Email?.message && errors?.Email?.message.toString()}
                                </span>
                            )}
                        </div>

                        <div className={classes.InputCont}>
                            <label>Телефон</label>
                            <Controller
                                control={control}
                                name="Phone"
                                rules={phoneValidationRules}
                                render={({ field }) => (
                                    <PhoneInput value={field.value} onChange={field.onChange} />
                                )}
                            />
                            {errors.Phone && showError("Phone")}
                        </div>

                        <div className={classes.InputCont}>
                            <label>Сообщение</label>
                            <Controller
                                control={control}
                                name="Message"
                                rules={messageValidationRules}
                                render={({ field: { onChange, value } }) => (
                                    <TextAreaInput
                                        style={{
                                            height: 200
                                        }}
                                        placeholder="Введите сообщение"
                                        value={value}
                                        onChange={(phone) => onChange(phone)}
                                    />
                                )}
                            />
                            {errors.Message && showError("Message")}

                            <div className={classes.ButtonCont}>
                                <PrimaryButton
                                    height={36}
                                    isLoading={false}
                                    onClick={onSubmit}
                                    style={buttonStyle}
                                    content="Отправить"
                                />
                            </div>
                        </div>
                    </form>
                </div>
            </div>
        </div>
    );
};

export default ContactsPage;